import React, { useState, useEffect, useCallback } from 'react';
import {
  DashboardReferenceWidgetInstanceConfig,
  QAWidgetInstanceConfig,
  ConvictionWidgetInstanceConfig,
  QADataContent,
  ConvictionDataContent,
  ImageRepositoryDataContent,
} from '../../../../../types';
import { dashboardService } from '../../../../../services';
import QAWidgetRenderer from '../../input/qa/QAWidgetRenderer';
import ConvictionWidgetRenderer from '../../input/conviction/ConvictionWidgetRenderer';
import { WidgetLoadingState, WidgetErrorState, WidgetEmptyState } from '../../../design';
import Button from '../../../../ui/Button';
import { getWidgetLabel, getWidgetIcon } from '../../../registry';

// Union type for all dashboard data content types (matches the child renderers' expected type)
type ChildRendererDataContent = QADataContent | ConvictionDataContent | ImageRepositoryDataContent;

interface DashboardReferenceWidgetRendererProps {
  config: DashboardReferenceWidgetInstanceConfig;
  readOnly?: boolean;
}

const DashboardReferenceWidgetRenderer: React.FC<DashboardReferenceWidgetRendererProps> = ({
  config,
  readOnly: propReadOnly = false,
}) => {
  const [sourceData, setSourceData] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Determine if widget is effectively read-only
  const isReadOnly = propReadOnly || config.readOnly;

  // Fetch source data from the referenced dashboard
  const fetchSourceData = useCallback(async () => {
    console.log('[DashboardReference] fetchSourceData called with config:', {
      sourceDashboardId: config.sourceDashboardId,
      sourceDataKey: config.sourceDataKey,
      sourceWidgetType: config.sourceWidgetType,
    });

    if (!config.sourceDashboardId || !config.sourceDataKey) {
      console.log('[DashboardReference] Missing sourceDashboardId or sourceDataKey, skipping fetch');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await dashboardService.getWidgetData(
        config.sourceDashboardId,
        config.sourceDataKey
      );

      console.log('[DashboardReference] Fetched data:', {
        sourceDashboardId: config.sourceDashboardId,
        sourceDataKey: config.sourceDataKey,
        exists: result.exists,
        data: result.data,
        dataType: result.data ? typeof result.data : 'null',
        dataKeys: result.data && typeof result.data === 'object' ? Object.keys(result.data as object) : 'not an object',
        hasCategories: result.data && typeof result.data === 'object' && 'categories' in (result.data as object),
        categoriesLength: result.data && typeof result.data === 'object' && 'categories' in (result.data as object)
          ? ((result.data as { categories?: unknown[] }).categories?.length ?? 'undefined')
          : 'N/A',
      });

      if (result.exists) {
        setSourceData(result.data);
        setLastUpdated(new Date());
      } else {
        console.warn('[DashboardReference] No data found at dataKey:', config.sourceDataKey);
        setSourceData(null);
      }
    } catch (err) {
      setError('Failed to load data from source dashboard');
      console.error('Dashboard reference fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [config.sourceDashboardId, config.sourceDataKey, config.sourceWidgetType]);

  // Initial fetch
  useEffect(() => {
    fetchSourceData();
  }, [fetchSourceData]);

  // Set up polling for live updates (every 30 seconds)
  useEffect(() => {
    if (!config.sourceDashboardId || !config.sourceDataKey) return;

    const interval = setInterval(fetchSourceData, 30000);
    return () => clearInterval(interval);
  }, [config.sourceDashboardId, config.sourceDataKey, fetchSourceData]);

  // Handle data changes (for editable mode)
  const handleDataChange = async (dataKey: string, newData: ChildRendererDataContent) => {
    if (isReadOnly || !config.sourceDashboardId || !config.sourceDataKey) return;

    try {
      setSaving(true);
      // Optimistically update local state
      setSourceData(newData);

      // Save back to source dashboard
      await dashboardService.updateWidgetData(
        config.sourceDashboardId,
        config.sourceDataKey,
        newData
      );
      setLastUpdated(new Date());
    } catch (err) {
      setError('Failed to save changes to source dashboard');
      // Revert on error
      fetchSourceData();
    } finally {
      setSaving(false);
    }
  };

  // Not configured
  if (!config.sourceDashboardId || !config.sourceDataKey || !config.sourceWidgetType) {
    return (
      <WidgetEmptyState
        icon={getWidgetIcon('dashboard_reference')}
        title="Dashboard Reference Not Configured"
        description="Configure the source dashboard and widget in the widget settings."
      />
    );
  }

  // Loading state
  if (loading) {
    return <WidgetLoadingState message="Loading data from source dashboard..." />;
  }

  // Error state
  if (error) {
    return <WidgetErrorState message="Error Loading Data" details={error} onRetry={fetchSourceData} />;
  }

  // No data found
  if (!sourceData) {
    return (
      <WidgetEmptyState
        icon={getWidgetIcon(config.sourceWidgetType)}
        title="No Data Found"
        description="The referenced widget has no data yet."
        action={
          <Button variant="outline" size="sm" onClick={fetchSourceData}>
            Refresh
          </Button>
        }
      />
    );
  }

  // At this point, we know these are not null (checked at line 115)
  const sourceWidgetType = config.sourceWidgetType!;
  const sourceDataKey = config.sourceDataKey!;

  // Build dashboard data object for child renderers
  const dashboardData: Record<string, unknown> = {
    [sourceDataKey]: sourceData,
  };

  // Render header with reference info
  const renderHeader = () => (
    <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-primary-50 to-secondary-50 border-b border-neutral-200 text-xs">
      <div className="flex items-center gap-2">
        <span className="text-lg">{getWidgetIcon(sourceWidgetType)}</span>
        <div>
          <span className="font-medium text-neutral-700">
            {config.displayTitle || getWidgetLabel(sourceWidgetType)}
          </span>
          {!isReadOnly && (
            <span className="ml-2 text-success-600 font-medium">• Editable</span>
          )}
          {isReadOnly && (
            <span className="ml-2 text-neutral-500">• Read-only</span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {saving && (
          <span className="text-primary-600">Saving...</span>
        )}
        {lastUpdated && (
          <span className="text-neutral-400">
            Updated {lastUpdated.toLocaleTimeString()}
          </span>
        )}
        <button
          onClick={fetchSourceData}
          disabled={loading}
          className="p-1 hover:bg-surface rounded text-neutral-400 hover:text-neutral-600"
          title="Refresh"
        >
          <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>
    </div>
  );

  // Render the appropriate sub-widget based on source widget type
  const renderWidget = () => {
    switch (sourceWidgetType) {
      case 'qa':
        // Create a QA config with the dataKey
        const qaConfig: QAWidgetInstanceConfig = {
          layout: 'vertical',
          showQuestionNumbers: true,
          allowAnswerEditing: !isReadOnly,
          dataKey: sourceDataKey,
        };
        return (
          <QAWidgetRenderer
            config={qaConfig}
            dashboardData={dashboardData}
            onDataChange={isReadOnly ? undefined : (handleDataChange as (dataKey: string, newData: ChildRendererDataContent) => void)}
            readOnly={isReadOnly}
          />
        );

      case 'conviction':
        // Create a Conviction config with the dataKey
        const convictionConfig: ConvictionWidgetInstanceConfig = {
          showLabels: true,
          allowComments: true,
          dataKey: sourceDataKey,
        };
        console.log('[DashboardReference] Rendering conviction with:', {
          sourceDataKey,
          dashboardDataKeys: Object.keys(dashboardData),
          sourceData,
          sourceDataType: typeof sourceData,
          hasCategories: sourceData && typeof sourceData === 'object' && 'categories' in sourceData,
          categoriesLength: sourceData && typeof sourceData === 'object' && 'categories' in sourceData
            ? ((sourceData as { categories?: unknown[] }).categories?.length ?? 'undefined')
            : 'N/A',
        });
        return (
          <ConvictionWidgetRenderer
            config={convictionConfig}
            dashboardData={dashboardData}
            onDataChange={isReadOnly ? undefined : (handleDataChange as (dataKey: string, newData: ChildRendererDataContent) => void)}
            readOnly={isReadOnly}
          />
        );

      case 'form':
        return (
          <WidgetEmptyState
            icon={getWidgetIcon('form')}
            title="Form Reference"
            description="Form widget rendering coming soon"
          />
        );

      default:
        return (
          <WidgetEmptyState
            icon={getWidgetIcon('qa')}
            title="Unknown Widget Type"
            description={`Widget type "${sourceWidgetType}" is not supported.`}
          />
        );
    }
  };

  return (
    <div className="h-full flex flex-col bg-surface">
      {renderHeader()}
      <div className="flex-1 overflow-hidden">
        {renderWidget()}
      </div>
    </div>
  );
};

export default DashboardReferenceWidgetRenderer;
